import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'motion/react';
import HeroAppPreview from './HeroAppPreview';
import { WAITLIST_BASE_COUNT, WAITLIST_BETA_DATE, daysUntil } from '@/seed/waitlist';

/**
 * Hero — above-the-fold marketing piece (HERO-01).
 *
 * Live-status chip + Cyrillic clamp() headline + subhead + dual CTA + HeroAppPreview.
 *
 * Animation: motion fade-in cascade respects prefers-reduced-motion (D-03, D-04).
 * When useReducedMotion() returns true, initial={false} disables all entry animations.
 *
 * Pre-launch CTA strategy (2026-04-27 UAT):
 *  - Primary: "Get early access" (orange-filled) → scrolls to #waitlist.
 *  - Secondary: "View live markets" (ghost outline) → scrolls to #markets.
 * Phase 5 CTA-01 will repurpose the primary CTA as the universal-link to the
 * native app once the beta is open and the app exists publicly.
 *
 * Live chip + sub-CTA caption pull WAITLIST_BASE_COUNT + WAITLIST_BETA_DATE
 * from `src/seed/waitlist.ts` so all FOMO surfaces stay in sync.
 *
 * CTAs use rounded-pill per RAD-01 brand radius override (UAT gap closure).
 * Headline max is clamp(40,5vw,72) — 96px still wrapped MK Cyrillic into 5
 * lines because long words ("предвидуваш") overflow the ~640px left column.
 * First sentence flows as one block (no forced <br />) so it breaks naturally.
 */
export default function Hero(): JSX.Element {
  const { t } = useTranslation();
  const reduce = useReducedMotion();

  // When reduced-motion is preferred, skip all entry animations (initial={false}).
  const initial = reduce ? false : { opacity: 0, y: 8 };
  const animate = { opacity: 1, y: 0 };

  const days = daysUntil(WAITLIST_BETA_DATE);
  const queueCount = WAITLIST_BASE_COUNT.toLocaleString('en-US');

  return (
    <section className="hero border-rule border-b py-24 md:py-32">
      <div className="mx-auto grid max-w-[1240px] grid-cols-1 items-end gap-12 px-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] lg:px-8">
        <div>
          {/* Live-status chip — replaces the thin eyebrow rule. Dark pill,
              pulsing orange dot, version + queue count + countdown.
              Echoes the terminal panel aesthetic on the right. */}
          <motion.div
            initial={initial}
            animate={animate}
            transition={reduce ? { duration: 0 } : { duration: 0.5 }}
            className="bg-ink text-paper rounded-pill mb-7 inline-flex max-w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-1.5 font-mono text-[11px] tracking-[0.18em] uppercase"
          >
            <span aria-hidden="true" className="bg-orange rounded-pill h-1.5 w-1.5 shrink-0" />
            <span className="text-orange">{t('hero.live.label')}</span>
            <span aria-hidden="true" className="text-paper/40">
              ·
            </span>
            <span>{t('hero.live.queue', { count: queueCount })}</span>
            <span aria-hidden="true" className="text-paper/40">
              ·
            </span>
            <span className="text-paper/80">{t('hero.live.daysLeft', { days })}</span>
          </motion.div>

          <motion.h1
            initial={initial}
            animate={animate}
            transition={reduce ? { duration: 0 } : { duration: 0.5, delay: 0.05 }}
            className="m-0 mb-6 font-sans text-[clamp(40px,5vw,72px)] leading-[1.02] font-semibold tracking-[-0.03em] [text-wrap:balance]"
          >
            {t('hero.headlineLine1')} {t('hero.headlineLine2')}{' '}
            <span className="text-orange">{t('hero.headlineAccent')}</span>
            {t('hero.headlineLine3')}
            <br />
            {t('hero.headlineLine4')}
          </motion.h1>

          <motion.p
            initial={initial}
            animate={animate}
            transition={reduce ? { duration: 0 } : { duration: 0.5, delay: 0.1 }}
            className="text-dim mb-10 max-w-[540px] text-[17px] leading-[1.55]"
          >
            {t('hero.subhead')}
          </motion.p>

          <motion.div
            initial={initial}
            animate={animate}
            transition={reduce ? { duration: 0 } : { duration: 0.5, delay: 0.15 }}
            className="flex flex-wrap items-center gap-3"
          >
            {/* Primary: orange-filled — visually echoes the bottom Waitlist CTA
                so the conversion path reads as one consistent thread. */}
            <a
              href="#waitlist"
              className="bg-orange text-ink border-orange hover:bg-ink hover:text-paper hover:border-ink rounded-pill inline-flex h-14 items-center justify-center gap-2.5 border px-7 font-mono text-[13px] font-semibold tracking-[0.18em] whitespace-nowrap uppercase transition-colors"
            >
              {t('hero.cta.joinBeta')}
              <span aria-hidden="true">→</span>
            </a>
            <a
              href="#markets"
              className="border-ink text-ink hover:bg-ink hover:text-paper rounded-pill inline-flex h-14 items-center justify-center border bg-transparent px-7 font-mono text-[13px] font-semibold tracking-[0.18em] whitespace-nowrap uppercase transition-colors"
            >
              {t('hero.cta.viewMarkets')}
            </a>
          </motion.div>

          {/* Sub-CTA caption: reinforces FOMO right where conversion happens. */}
          <motion.p
            initial={initial}
            animate={animate}
            transition={reduce ? { duration: 0 } : { duration: 0.5, delay: 0.2 }}
            className="text-dim mt-5 font-mono text-[11px] tracking-[0.18em] uppercase"
          >
            {t('hero.subCta', { days, count: queueCount })}
          </motion.p>
        </div>

        <motion.div
          initial={initial}
          animate={animate}
          transition={reduce ? { duration: 0 } : { duration: 0.5, delay: 0.25 }}
        >
          <HeroAppPreview />
        </motion.div>
      </div>
    </section>
  );
}
